import { useCallback, useState } from 'react';
import { BilberryLeadFormData } from '@repo/types';
import {
    validateDateRange,
    validateEmail,
    validateTextField,
} from '@repo/common-utils/validation-helper';
import { DateRange } from '@mui/x-date-pickers-pro';
import { TZDate } from '@repo/tzdate';
import { useLocale } from '@repo/i18n';
import { postLeadForm } from '@repo/widget-utils/services/fetchers/product';

function getLeadFormDateString(dayjsDate: TZDate | null, locale: string) {
    return dayjsDate ? dayjsDate.forceLocalTz().locale(locale).format('YYYY-MM-DD HH:mm:ss') : '';
}

export default function useLeadsState(
    hideRequestDate = false,
    hideQuantityPicker = false,
    getTotalGuests: () => number,
) {
    const { locale } = useLocale();
    const [description, setDescription] = useState('');
    const [descriptionValid, setDescriptionValid] = useState(true);
    const [dateRange, setDateRange] = useState<DateRange<TZDate>>([
        TZDate.now().hour(0).minute(0).second(0),
        TZDate.now().hour(0).minute(0).second(0),
    ]);
    const [dateRangeValid, setDateRangeValid] = useState(true);
    const [name, setName] = useState('');
    const [phone, setPhone] = useState('');
    const [email, setEmail] = useState('');
    const [nameValid, setNameValid] = useState(true);
    const [emailValid, setEmailValid] = useState(true);
    const [showConfirm, setShowConfirm] = useState(false);
    const [showSubmitError, setShowSubmitError] = useState(false);

    const [dateRangeStart, dateRangeEnd] = dateRange;

    const onClickBook = useCallback(() => {
        const fn = async () => {
            const formValidation = validateFormData(
                dateRange,
                description,
                name,
                email,
                hideRequestDate,
            );
            setDateRangeValid(formValidation.dateRange);
            setDescriptionValid(formValidation.description);
            setNameValid(formValidation.name);
            setEmailValid(formValidation.email);
            if (!formValidation.formValid) return;

            const title = document.title ?? window.location.href;

            const leadsFormData: BilberryLeadFormData = {
                title,
                name,
                email,
                description,
                phone: phone ? phone : undefined,
                // eslint-disable-next-line @typescript-eslint/naming-convention
                group_size: hideQuantityPicker ? 0 : getTotalGuests(),
                ...(hideRequestDate
                    ? {}
                    : {
                          start: getLeadFormDateString(dateRangeStart, locale),
                          end: getLeadFormDateString(dateRangeEnd ?? dateRangeStart, locale),
                      }),
            };

            try {
                await postLeadForm(leadsFormData);
            } catch (_e) {
                setShowSubmitError(true);
            }

            setShowConfirm(true);
        };
        fn().then();
    }, [
        dateRange,
        description,
        name,
        email,
        phone,
        getTotalGuests,
        dateRangeStart,
        locale,
        dateRangeEnd,
        hideRequestDate,
        hideQuantityPicker,
    ]);

    const onClickGotIt = useCallback(() => {
        setDateRange([
            TZDate.now().hour(0).minute(0).second(0),
            TZDate.now().hour(0).minute(0).second(0),
        ]);
        setDescription('');
        setName('');
        setPhone('');
        setEmail('');
        setDateRangeValid(true);
        setDescriptionValid(true);
        setNameValid(true);
        setEmailValid(true);
        setShowConfirm(false);
        setShowSubmitError(false);
    }, []);

    return {
        description,
        setDescription,
        descriptionValid,
        dateRange,
        setDateRange,
        dateRangeValid,
        name,
        setName,
        nameValid,
        phone,
        setPhone,
        email,
        setEmail,
        emailValid,
        showConfirm,
        showSubmitError,
        onClickBook,
        onClickGotIt,
    };
}

function validateFormData(
    dateRange: DateRange<TZDate>,
    description: string,
    name: string,
    email: string,
    hideRequestDate: boolean,
) {
    const res = {
        dateRange: hideRequestDate ? true : validateDateRange(dateRange),
        description: validateTextField(description),
        name: validateTextField(name),
        email: validateEmail(email),
        formValid: true,
    };
    res.formValid = Object.values(res).every(Boolean);
    return res;
}
